import React, { useRef, useEffect, useState } from 'react'
import './SignaturePad.css'

interface SignaturePadProps {
  onSignatureChange: (signatureBase64: string) => void
  width?: number
  height?: number
  className?: string
  initialSignature?: string | null
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  onSignatureChange,
  width = 400,
  height = 200,
  className = '',
  initialSignature = null
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    canvas.width = width
    canvas.height = height

    // Set drawing styles
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // Clear canvas with white background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)

    // Load initial signature if provided
    if (initialSignature) {
      loadSignature(initialSignature)
    }
  }, [width, height, initialSignature])

  const loadSignature = (signatureBase64: string) => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx || !canvas) return

    const img = new Image()
    img.onload = () => {
      // Clear canvas first
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, width, height)
      
      // Draw the signature image
      ctx.drawImage(img, 0, 0, width, height)
      setHasSignature(true)
      onSignatureChange(signatureBase64)
    }
    img.src = signatureBase64
  }

  const getEventPos = (e: MouseEvent | TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    if ('touches' in e) {
      // Touch event
      const touch = e.touches[0] || e.changedTouches[0]
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY
      }
    } else {
      // Mouse event
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      }
    }
  }

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    setIsDrawing(true)
    setHasSignature(true)

    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return

    const pos = getEventPos(e.nativeEvent)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    if (!isDrawing) return

    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return

    const pos = getEventPos(e.nativeEvent)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
  }

  const stopDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    if (!isDrawing) return

    setIsDrawing(false)
    const canvas = canvasRef.current
    if (!canvas) return

    // Convert to base64 and call callback
    const dataURL = canvas.toDataURL('image/png')
    onSignatureChange(dataURL)
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return

    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    setHasSignature(false)
    onSignatureChange('')
  }

  const isEmpty = () => {
    return !hasSignature
  }

  return (
    <div className={`signature-pad-container ${className}`}>
      <canvas
        ref={canvasRef}
        className="signature-canvas"
        style={{
          border: '2px solid #e9ecef',
          borderRadius: '5px',
          cursor: 'crosshair',
          touchAction: 'none'
        }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <div className="signature-controls" style={{ marginTop: '10px' }}>
        <button
          type="button"
          onClick={clearSignature}
          className="btn btn-secondary"
          style={{ marginRight: '10px' }}
        >
          Clear
        </button>
        {isEmpty() && (
          <small className="text-muted">Please sign above</small>
        )}
      </div>
    </div>
  )
}

export default SignaturePad 